import { useState, useCallback } from "react";
import type { RefObject } from "react";

// Плагин eslint-plugin-import не умеет правильно определять экспорты из package.json
// eslint-disable-next-line import/no-unresolved
import throttle from "lodash/throttle";

import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect";

const THROTTLE_DELAY = 50;

export type DropdownSide = "top" | "bottom" | "right" | "left" | "custom";
export type DropdownAlignment = "start" | "center" | "end" | "custom";
export type DropdownPosition = {
    left: string;
    top: string;
};

interface IDropdownPosition {
    targetRef: RefObject<HTMLElement>;
    containerRef: RefObject<HTMLElement>;
    side: DropdownSide;
    alignment: DropdownAlignment;
    adjustmentLocked: boolean;
}

const useDropdownPosition = ({
    targetRef,
    containerRef,
    side,
    alignment,
    adjustmentLocked,
}: IDropdownPosition) => {
    const [startCalc, setStartCalc] = useState(false);
    const [position, setPosition] = useState<DropdownPosition>();
    const [dropdownSide, setDropdownSide] = useState<DropdownSide>(side);
    const [dropdownAlignment, setDropdownAlignment] =
        useState<DropdownAlignment>(alignment);

    /** Получаем все rect'ы, на основе которых будем производить высисления дальше */
    const getRects = useCallback(() => {
        const targetElement = targetRef.current;
        const dropdownElement = containerRef.current;

        if (!targetElement || !dropdownElement) {
            return {
                target: null,
                dropdown: null,
            };
        }

        return {
            target: targetElement.getBoundingClientRect(),
            dropdown: dropdownElement.getBoundingClientRect(),
        };
    }, [containerRef, targetRef]);

    /** Получаем количество свободного места для рендера компонента на основе таргета */
    const getSpace = useCallback((targetRect: DOMRect) => {
        const spaceAbove = targetRect.top;
        const spaceBelow = window.innerHeight - targetRect.bottom;
        const spaceLeft = targetRect.left;
        const spaceRight = window.innerWidth - targetRect.right;
        return {
            spaceAbove,
            spaceBelow,
            spaceLeft,
            spaceRight,
        };
    }, []);

    /** Вычисляем position на основе side и alignment */
    const calcPositionWithAdjustment = useCallback(
        (
            targetRect: DOMRect,
            dropdownRect: DOMRect,
            dropdownSide: DropdownSide,
            dropdownAlignment: DropdownAlignment,
        ) => {
            const dropdownWidth = dropdownRect.width;
            const dropdownHeight = dropdownRect.height;

            // Учитываем текущее смещение по скроллу страницы
            const scrollTop =
                window.scrollY || document.documentElement.scrollTop;
            const scrollLeft =
                window.scrollX || document.documentElement.scrollLeft;

            let alignmentWidthOffset = 0;
            let alignmentHeightOffset = 0;

            // Если alignment === "start", сдвигаем в сторону end
            if (dropdownAlignment === "center") {
                alignmentWidthOffset = (targetRect.width - dropdownWidth) / 2;
                alignmentHeightOffset =
                    (targetRect.height - dropdownHeight) / 2;
            }
            // Если alignment === "end", сдвигаем в сторону start
            if (dropdownAlignment === "end") {
                alignmentWidthOffset = -dropdownWidth + targetRect.width;
                alignmentHeightOffset = -dropdownHeight + targetRect.height;
            }

            let left = targetRect.left + scrollLeft;
            let top = targetRect.bottom + scrollTop;

            // Если сверху или снизу то выравнивание по оси x иначе y
            if (dropdownSide === "top" || dropdownSide === "bottom") {
                left += alignmentWidthOffset;
            } else {
                top += alignmentHeightOffset;
            }

            if (dropdownSide === "top") {
                top -= targetRect.height + dropdownHeight;
            }

            if (dropdownSide === "right") {
                left += targetRect.width;
                top -= targetRect.height;
            }

            if (dropdownSide === "left") {
                left -= dropdownWidth;
                top -= targetRect.height;
            }

            setPosition({
                left: `${left}px`,
                top: `${top}px`,
            });
        },
        [],
    );

    /** Вычисляем alignment на основе side */
    const calcDropdownAlignment = useCallback(
        (
            targetRect: DOMRect,
            dropdownRect: DOMRect,
            dropdownSide: DropdownSide,
        ) => {
            const { spaceAbove, spaceBelow, spaceLeft, spaceRight } =
                getSpace(targetRect);
            const { width: dropdownWidth, height: dropdownHeight } =
                dropdownRect;

            /** Устанавливаем первичное значение относительно переданного alignment'а и имеющегося места */
            setDropdownAlignment((prevState) => {
                if (dropdownSide === "bottom" || dropdownSide === "top") {
                    if (alignment === "center") {
                        if (
                            spaceRight >=
                                (dropdownWidth - targetRect.width) / 2 &&
                            spaceLeft >= (dropdownWidth - targetRect.width) / 2
                        ) {
                            return "center";
                        }
                    } else if (alignment === "start") {
                        if (spaceRight >= dropdownWidth - targetRect.width) {
                            return "start";
                        }
                    } else if (alignment === "end") {
                        if (spaceLeft >= dropdownWidth - targetRect.width) {
                            return "end";
                        }
                    }
                } else if (
                    dropdownSide === "left" ||
                    dropdownSide === "right"
                ) {
                    if (alignment === "center") {
                        if (
                            spaceBelow >=
                                (dropdownHeight - targetRect.height) / 2 &&
                            spaceAbove >=
                                (dropdownHeight - targetRect.height) / 2
                        ) {
                            return "center";
                        }
                    } else if (alignment === "start") {
                        if (spaceBelow >= dropdownHeight - targetRect.height) {
                            return "start";
                        }
                    } else if (alignment === "end") {
                        if (spaceAbove >= dropdownHeight - targetRect.height) {
                            return "end";
                        }
                    }
                }
                return prevState;
            });

            /** Перевычисляем alignment, согласно правилам позиционирования при нехватке места */
            setDropdownAlignment((prevDropdownAlignment) => {
                let currentDropdownAlignment = prevDropdownAlignment;

                // Меняем alignment если не хватает места
                if (currentDropdownAlignment === "start") {
                    if (dropdownSide === "bottom" || dropdownSide === "top") {
                        if (spaceRight < dropdownWidth - targetRect.width) {
                            if (spaceLeft >= dropdownWidth - targetRect.width) {
                                currentDropdownAlignment = "end";
                            } else {
                                currentDropdownAlignment = "center";
                            }
                        }
                    }

                    if (dropdownSide === "left" || dropdownSide === "right") {
                        if (spaceBelow < dropdownHeight - targetRect.height) {
                            if (
                                spaceAbove >=
                                dropdownHeight - targetRect.height
                            ) {
                                currentDropdownAlignment = "end";
                            } else {
                                currentDropdownAlignment = "center";
                            }
                        }
                    }
                } else if (currentDropdownAlignment === "end") {
                    if (dropdownSide === "bottom" || dropdownSide === "top") {
                        if (spaceLeft < dropdownWidth - targetRect.width) {
                            if (
                                spaceRight >=
                                dropdownWidth - targetRect.width
                            ) {
                                currentDropdownAlignment = "start";
                            } else {
                                currentDropdownAlignment = "center";
                            }
                        }
                    }

                    if (dropdownSide === "left" || dropdownSide === "right") {
                        if (spaceAbove < dropdownHeight - targetRect.height) {
                            if (
                                spaceBelow >=
                                dropdownHeight - targetRect.height
                            ) {
                                currentDropdownAlignment = "start";
                            } else {
                                currentDropdownAlignment = "center";
                            }
                        }
                    }
                } else if (currentDropdownAlignment === "center") {
                    if (dropdownSide === "bottom" || dropdownSide === "top") {
                        if (
                            spaceLeft <
                            (dropdownWidth - targetRect.width) / 2
                        ) {
                            if (
                                spaceRight >=
                                dropdownWidth - targetRect.width
                            ) {
                                currentDropdownAlignment = "start";
                            }
                        } else if (
                            spaceRight <
                            (dropdownWidth - targetRect.width) / 2
                        ) {
                            if (spaceLeft >= dropdownWidth - targetRect.width) {
                                currentDropdownAlignment = "end";
                            }
                        } else {
                            currentDropdownAlignment = "center";
                        }
                    }

                    if (dropdownSide === "left" || dropdownSide === "right") {
                        if (
                            spaceAbove <
                            (dropdownHeight - targetRect.height) / 2
                        ) {
                            if (
                                spaceBelow >=
                                dropdownHeight - targetRect.height
                            ) {
                                currentDropdownAlignment = "start";
                            }
                        } else if (
                            spaceBelow <
                            (dropdownHeight - targetRect.height) / 2
                        ) {
                            if (
                                spaceAbove >=
                                dropdownHeight - targetRect.height
                            ) {
                                currentDropdownAlignment = "end";
                            }
                        } else {
                            currentDropdownAlignment = "center";
                        }
                    }
                }
                calcPositionWithAdjustment(
                    targetRect,
                    dropdownRect,
                    dropdownSide,
                    currentDropdownAlignment,
                );
                return currentDropdownAlignment;
            });
        },
        [alignment, calcPositionWithAdjustment, getSpace],
    );

    /**
     * Здесь будем выполнять все функции вычисления позиционирования по очереди:
     * 1. Вычисляем side, так как от него зависит alignment и position;
     * 2. Затем alignment, так как от него также зависит position;
     * 3. Вычисляем position.
     */
    const calcPositionWithoutAdjustment = useCallback(
        (targetRect: DOMRect, dropdownRect: DOMRect) => {
            const { spaceAbove, spaceBelow, spaceLeft, spaceRight } =
                getSpace(targetRect);
            const { width: dropdownWidth, height: dropdownHeight } =
                dropdownRect;

            /** Устанавливаем первичное значение относительно переданного side'а и имеющегося места */
            setDropdownSide((prevState) => {
                if (side === "top") {
                    if (spaceAbove >= dropdownHeight) {
                        return "top";
                    }
                } else if (side === "bottom") {
                    if (spaceBelow >= dropdownHeight) {
                        return "bottom";
                    }
                } else if (side === "left") {
                    if (spaceLeft >= dropdownHeight) {
                        return "left";
                    }
                } else if (side === "right") {
                    if (spaceRight >= dropdownHeight) {
                        return "right";
                    }
                }
                return prevState;
            });

            /** Перевычисляем side, согласно правилам позиционирования при нехватке места */
            setDropdownSide((prevDropdownSide) => {
                let currentDropdownSide = prevDropdownSide;

                // Меняем сторону отображения если не хватает места и есть место с другой стороны
                if (prevDropdownSide === "top" && spaceAbove < dropdownHeight) {
                    if (spaceBelow >= dropdownHeight) {
                        currentDropdownSide = "bottom";
                    }
                }

                if (
                    prevDropdownSide === "bottom" &&
                    spaceBelow < dropdownHeight
                ) {
                    if (spaceAbove >= dropdownHeight) {
                        currentDropdownSide = "top";
                    }
                }

                if (prevDropdownSide === "left" && spaceLeft < dropdownWidth) {
                    if (spaceRight >= dropdownHeight) {
                        currentDropdownSide = "right";
                    }
                }

                if (
                    prevDropdownSide === "right" &&
                    spaceRight < dropdownWidth
                ) {
                    if (spaceLeft >= dropdownHeight) {
                        currentDropdownSide = "left";
                    }
                }

                calcDropdownAlignment(
                    targetRect,
                    dropdownRect,
                    currentDropdownSide,
                );

                return currentDropdownSide;
            });
        },
        [calcDropdownAlignment, side, getSpace],
    );

    const calcPosition = useCallback(() => {
        const { target, dropdown } = getRects();
        if (!target || !dropdown) {
            return;
        }
        if (adjustmentLocked) {
            calcPositionWithAdjustment(
                target,
                dropdown,
                dropdownSide,
                dropdownAlignment,
            );
        } else {
            calcPositionWithoutAdjustment(target, dropdown);
        }
    }, [
        adjustmentLocked,
        calcPositionWithAdjustment,
        calcPositionWithoutAdjustment,
        dropdownAlignment,
        dropdownSide,
        getRects,
    ]);

    /** Начинаем работу только в том случае, если таргет попал в область видимости */
    useIsomorphicLayoutEffect(() => {
        const target = targetRef.current;
        if (!target) {
            return;
        }

        /** При adjustmentLock компонент не будет в DOM всегда, поэтому отслеживать его через IO нам не нужно */
        if (adjustmentLocked) {
            setStartCalc(true);
            return () => {
                setStartCalc(false);
            };
        }

        const intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setStartCalc(entry.isIntersecting);
                });
            },
            {
                threshold: 0,
            },
        );

        intersectionObserver.observe(target);

        return () => {
            intersectionObserver.disconnect();
        };
    }, [containerRef, targetRef, adjustmentLocked]);

    /**
     * Вешаем слушатели на scroll и resize, чтобы всегда считать актуальное позиционирование, вычисляем предварительную позицию
     */
    useIsomorphicLayoutEffect(() => {
        if (!startCalc) {
            return;
        }
        calcPosition();

        const throttled = throttle(calcPosition, THROTTLE_DELAY);

        window.addEventListener("resize", throttled);
        window.addEventListener("scroll", throttled, false);

        return () => {
            window.removeEventListener("resize", throttled);
            window.removeEventListener("scroll", throttled, false);
        };
    }, [startCalc, calcPosition]);

    return { position, dropdownSide, dropdownAlignment };
};

export default useDropdownPosition;
